import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import AdminPageHeader from "../components/admin-page-header";
import MatrixView from "../components/access-control/MatrixView";
import UsersView from "../components/access-control/UsersView";
import PreviewView from "../components/access-control/PreviewView";
import ChangeLogView from "../components/access-control/ChangeLogView";
import ParityView from "../components/access-control/ParityView";
import SnapshotsView from "../components/access-control/SnapshotsView";
import FixedRulesView from "../components/access-control/FixedRulesView";
import {
    LevelOrInherit, applyEdits, cellId, diffChanges, parseCellId, storedLevel,
} from "../components/access-control/acHelpers";
import { AccessControlConfig } from "../components/access-control/types";
import "../styles/admin-access-control.css";

type ViewTab = "matrix" | "users" | "preview" | "changelog" | "parity" | "snapshots" | "fixed";

const VIEW_TABS: { id: ViewTab; labelKey: string }[] = [
    { id: "matrix", labelKey: "ac_view_matrix" },
    { id: "users", labelKey: "ac_view_users" },
    { id: "preview", labelKey: "ac_view_preview" },
    { id: "changelog", labelKey: "ac_view_changelog" },
    { id: "parity", labelKey: "ac_view_parity" },
    { id: "snapshots", labelKey: "ac_view_snapshots" },
    { id: "fixed", labelKey: "ac_view_fixed" },
];

interface SettingsRow {
    warehouse: string;
}

const AdminAccessControl = () => {
    const { t } = useTranslation();
    const [config, setConfig] = useState<AccessControlConfig | null>(null);
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [view, setView] = useState<ViewTab>("matrix");
    const [edits, setEdits] = useState<Record<string, LevelOrInherit>>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [conflict, setConflict] = useState(false);

    const loadConfig = useCallback(async () => {
        setLoadError(null);
        const res = await apiFetch("/access-control");
        if (res.status === 401) { logoutProcessor(); return null; }
        // A refusal is not a server error: say so (seen on staging 2026-09-30 when a read grant met
        // a floor the guard still enforced).
        if (res.status === 403) { setLoadError(t("ac_no_access")); return null; }
        if (!res.ok) { setLoadError(t("server_error")); return null; }
        const body: AccessControlConfig = await res.json();
        setConfig(body);
        setEdits({});
        setConflict(false);
        return body;
    }, [t]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [, settingsRes] = await Promise.all([
                    loadConfig(),
                    apiFetch("/settings"),
                ]);
                if (settingsRes.ok) {
                    const rows: SettingsRow[] = await settingsRes.json();
                    if (Array.isArray(rows)) setWarehouses(rows.map(r => r.warehouse).filter(Boolean));
                }
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mergedRules = useMemo(() => {
        if (!config) return { positions: {}, users: {} };
        return applyEdits(config.rules, edits);
    }, [config, edits]);

    const pendingCount = Object.keys(edits).length;

    const cycleCell = (subjectType: "position" | "user", subjectId: string, warehouse: string | null, key: string, next: LevelOrInherit) => {
        const id = cellId(subjectType, subjectId, warehouse, key);
        setEdits(prev => ({ ...prev, [id]: next }));
    };

    const discard = () => setEdits({});

    const save = async () => {
        if (!config || pendingCount === 0) return;
        setSaving(true);
        setSaveError(null);
        setConflict(false);
        try {
            const originalMap: Record<string, LevelOrInherit> = {};
            Object.keys(edits).forEach(id => {
                const { subjectType, subjectId, warehouse, key } = parseCellId(id);
                originalMap[id] = storedLevel({ rules: config.rules, subjectType, subjectId, warehouse, key });
            });
            const changes = diffChanges(originalMap, edits);
            if (changes.length === 0) { setEdits({}); return; }

            const res = await apiFetch("/access-control/rules", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ base_revision: config.revision, changes }),
            });
            if (res.status === 401) { logoutProcessor(); return; }
            if (res.status === 409) { setConflict(true); return; }
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setSaveError(body?.message ?? t("server_error"));
                return;
            }
            // Reload so the matrix shows exactly what is stored, then say so
            // out loud: without this the bar silently flipped back to "no
            // unsaved changes" and a successful save looked like nothing had
            // happened (user report, 2026-09-21).
            const next = await loadConfig();
            toast.success(t("ac_saved_revision", { revision: next?.revision ?? config.revision + 1 }), { toastId: "ac-saved" });
        } catch {
            setSaveError(t("server_error"));
        } finally {
            setSaving(false);
        }
    };

    const reloadAfterConflict = async () => {
        await loadConfig();
    };

    return (
        <div className="page-data page-data-wide">
            <AdminPageHeader
                titleKey="ac_title"
                backTo="/admin/settings"
                hideSearch
                crumbs={[
                    { labelKey: "admin_panel", to: "/admin" },
                    { labelKey: "settings", to: "/admin/settings" },
                    { labelKey: "ac_title" },
                ]}
            />

            {loading && <div className="admin-settings-loading">{t("loading")}</div>}
            {loadError && <p className="ac-hint ac-hint--error">{loadError}</p>}

            {config && (
                <>
                    {config.tablesMissing && (
                        <div className="ac-banner ac-banner--danger">
                            <AlertTriangle size={18} />
                            <span>{t("ac_tables_missing")}</span>
                        </div>
                    )}
                    {!config.tablesMissing && config.mode !== "active" && (
                        <div className="ac-banner ac-banner--info">
                            <span>{t("ac_preview_only_banner")}</span>
                        </div>
                    )}

                    {conflict && (
                        <div className="ac-banner ac-banner--danger">
                            <AlertTriangle size={18} />
                            <span>{t("ac_revision_conflict")}</span>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={reloadAfterConflict}>
                                {t("ac_reload_and_retry")}
                            </button>
                        </div>
                    )}
                    {saveError && <p className="ac-hint ac-hint--error">{saveError}</p>}

                    <div className="ac-view-tabs" role="tablist">
                        {VIEW_TABS.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={view === tab.id}
                                className={`ac-view-tab${view === tab.id ? " ac-view-tab--active" : ""}`}
                                onClick={() => setView(tab.id)}
                            >
                                {t(tab.labelKey)}
                            </button>
                        ))}
                    </div>

                    {/* Only while there is something to save: a permanent bar reading
                        "No unsaved changes" next to greyed-out buttons read as "did my
                        save go through?". Saved state is confirmed by the toast above. */}
                    {(view === "matrix" || view === "users") && !config.tablesMissing && (pendingCount > 0 || saving) && (
                        <div className="ac-save-bar">
                            <span className="ac-pending-count">
                                {pendingCount > 0 ? t("ac_pending_changes", { count: pendingCount }) : t("ac_no_pending_changes")}
                            </span>
                            <div className="ac-save-bar-actions">
                                <button type="button" className="btn btn-ghost btn-sm" onClick={discard} disabled={pendingCount === 0 || saving}>
                                    {t("ac_discard")}
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={pendingCount === 0 || saving}>
                                    {saving ? t("saving") : t("save")}
                                </button>
                            </div>
                        </div>
                    )}

                    {view === "matrix" && (
                        <MatrixView
                            config={config}
                            warehouses={warehouses}
                            rules={mergedRules}
                            onCycle={(subjectId, warehouse, key, next) => cycleCell("position", subjectId, warehouse, key, next)}
                        />
                    )}
                    {view === "users" && (
                        <UsersView
                            config={config}
                            rules={mergedRules}
                            onCycle={(uid, warehouse, key, next) => cycleCell("user", uid, warehouse, key, next)}
                        />
                    )}
                    {view === "preview" && <PreviewView config={config} warehouses={warehouses} />}
                    {view === "changelog" && <ChangeLogView />}
                    {view === "parity" && <ParityView />}
                    {view === "snapshots" && (
                        <SnapshotsView revision={config.revision} onRestored={() => loadConfig()} />
                    )}
                    {view === "fixed" && <FixedRulesView config={config} />}
                </>
            )}
        </div>
    );
};

export default AdminAccessControl;
